import { Injectable, signal } from '@angular/core';

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  progress?: number;
  startTime?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toasts = signal<Toast[]>([]);
  private idCounter = 0;

  // Read-only computed signals
  readonly activeToasts = this.toasts.asReadonly();

  show(message: string, type: Toast['type'] = 'success', duration = 3000): void {
    const id = `toast-${++this.idCounter}`;
    const startTime = Date.now();
    const toast: Toast = { id, message, type, duration, progress: 100, startTime };

    this.toasts.update((toasts) => {
      const updatedToasts = [toast, ...toasts];
      // Limitar a máximo 2 toasts simultáneos - eliminar el más antiguo si hay más de 2
      return updatedToasts.slice(0, 2);
    });

    // Auto-dismiss after duration with progress bar
    if (duration > 0) {
      // Update progress every 100ms
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.max(0, 100 - (elapsed / duration) * 100);

        this.toasts.update((toasts) => toasts.map((t) => (t.id === id ? { ...t, progress } : t)));

        if (progress <= 0) {
          clearInterval(progressInterval);
        }
      }, 100);

      setTimeout(() => {
        clearInterval(progressInterval);
        this.dismiss(id);
      }, duration);
    }
  }

  success(message: string, duration?: number): void {
    this.show(message, 'success', duration);
  }

  error(message: string, duration?: number): void {
    this.show(message, 'error', duration);
  }

  info(message: string, duration?: number): void {
    this.show(message, 'info', duration);
  }

  warning(message: string, duration?: number): void {
    this.show(message, 'warning', duration);
  }

  dismiss(id: string): void {
    this.toasts.update((toasts) => toasts.filter((toast) => toast.id !== id));
  }

  clear(): void {
    this.toasts.set([]);
  }
}
